import React from 'react';
import { getBezierPath, getSmoothStepPath, getStraightPath, EdgeLabelRenderer } from 'reactflow';
import { useDesignerDerived } from '../DesignerDerivedContext';

// Stable small offset per edge id, used to spread parallel edges apart inside
// a layout gutter so they don't run on top of each other. Deterministic (same
// edge always gets the same lane), spread across 5 lanes 14 px apart.
const laneOffset = (id) => {
  let h = 0;
  const s = String(id || '');
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return ((Math.abs(h) % 5) - 2) * 14; // -28, -14, 0, 14, 28
};

const ThickPathEdge = ({ id, sourceX, sourceY, targetX, targetY, selected, data, sourcePosition, targetPosition }) => {
  const { edgeSourceTypes, defaultEdgeType, edgeFontPx, arrowSize } = useDesignerDerived();
  const labelFontSize = edgeFontPx || 14;
  const sourceIsDecision = edgeSourceTypes.get(id) === 'decision';
  const edgeType = data?.edgeType || defaultEdgeType || 'default';
  const pathParams = { sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition };

  // Orthogonal edges get a per-edge lane inside the gutter so parallel runs
  // (and their labels) don't coincide.
  const centerX = (sourceX + targetX) / 2 + laneOffset(id);

  let edgePath, labelX, labelY;
  switch (edgeType) {
    case 'smoothstep':
      [edgePath, labelX, labelY] = getSmoothStepPath({ ...pathParams, centerX });
      break;
    case 'step':
      [edgePath, labelX, labelY] = getSmoothStepPath({ ...pathParams, borderRadius: 0, centerX });
      break;
    case 'straight':
      [edgePath, labelX, labelY] = getStraightPath(pathParams);
      break;
    default:
      [edgePath, labelX, labelY] = getBezierPath(pathParams);
  }

  const pathClass = `react-flow__edge-path ${selected ? 'edge-selected' : ''}`;
  const level = data?.level;
  const isOther = data?.isDefault === true;
  const lineType = data?.style || 'normal';

  let strokeDasharray;
  if (isOther) {
    strokeDasharray = '8 4';
  } else {
    switch (lineType) {
      case 'conditional': strokeDasharray = '10 5'; break;
      case 'fallback': strokeDasharray = '3 4'; break;
      case 'package': strokeDasharray = '12 4 3 4'; break;
      default: strokeDasharray = undefined;
    }
  }

  const strokeColor = level === 'error' ? '#dc3545'
    : level === 'warning' ? '#ffc107'
      : lineType === 'package' ? '#28a745'
        : isOther ? '#999'
          : (selected ? '#007bff' : '#999');
  const strokeW = selected ? 8 : 6;

  // The label is what the edge displays — always (ruling 2026-08-02). For
  // decision branches it is also the routing expression the kernel evaluates,
  // so showing anything else would hide the survey's actual routing. Legacy
  // edges authored before labels-as-expressions may carry only a condition
  // string; fall back to it so old graphs don't render blank.
  let labelText;
  if (sourceIsDecision) {
    const legacyCondition = (data?.condition || '').trim();
    labelText = data?.label || legacyCondition || data?.branch_id || (isOther ? 'else' : '');
  } else {
    labelText = isOther && !data?.label ? 'Other' : (data?.label || '');
  }
  const codeText = data?.code || '';
  const displayLabel = labelText;
  const hasLabel = displayLabel || codeText;

  const fullLabel = `${codeText ? `[${codeText}] ` : ''}${displayLabel}`;
  const LABEL_MAXW = 200;

  // Rotate the label to follow a predominantly vertical edge (90°, reading
  // bottom-to-top), so it rides along the connector instead of sprawling
  // sideways into neighboring nodes. Diagonal-ish edges stay horizontal —
  // the 1.5 factor keeps rotation for clearly vertical runs only.
  const rotated = Math.abs(targetY - sourceY) > 1.5 * Math.abs(targetX - sourceX);

  // Directional arrowhead at the target end, indicating flow direction for
  // survey authors. Sized in absolute (userSpaceOnUse) units so it stays a
  // fixed size regardless of the thick stroke width, and colored to match this
  // edge. Size comes from Canvas appearance (0 = hidden). Selected edges get a
  // small bump to stay proportional to their thicker stroke.
  const baseArrow = arrowSize == null ? 18 : arrowSize;
  const showArrow = baseArrow > 0;
  const markerId = `arrow-${id}`;
  const ARROW = selected ? baseArrow + 2 : baseArrow;

  return (
    <g className="react-flow__edge" data-id={id}>
      {showArrow ? (
        <defs>
          <marker
            id={markerId}
            viewBox="0 0 12 12"
            refX={10}
            refY={6}
            markerWidth={ARROW}
            markerHeight={ARROW}
            markerUnits="userSpaceOnUse"
            orient="auto"
          >
            <path d="M 0 0 L 12 6 L 0 12 z" fill={strokeColor} />
          </marker>
        </defs>
      ) : null}
      <path
        id={id}
        className={pathClass}
        d={edgePath}
        fill="none"
        markerEnd={showArrow ? `url(#${markerId})` : undefined}
        style={{ stroke: strokeColor, strokeWidth: strokeW, strokeDasharray }}
      />
      {hasLabel ? (
        // EdgeLabelRenderer portals the label out of the edge SVG into a div
        // layer, and the explicit zIndex lifts it above nodes — labels no
        // longer disappear when an edge passes behind a node card.
        <EdgeLabelRenderer>
          <div
            title={fullLabel}
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)${rotated ? ' rotate(-90deg)' : ''}`,
              pointerEvents: 'none',
              zIndex: 1000,
              fontSize: labelFontSize * 1.5,
              textAlign: 'center',
              lineHeight: 1.2,
              maxWidth: `${LABEL_MAXW}px`,
              boxSizing: 'border-box',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              borderRadius: '6px',
              padding: '3px 8px',
              // Selected edges flag their label with the design system's yellow
              // tag treatment (tag.yellow: #FFF9E6 on #7A6A3A). The pale fill is
              // subtle at label size over a busy canvas, so the amber border and
              // ring carry the signal. Deselecting restores white/grey.
              background: selected ? '#FFF9E6' : (isOther ? '#f0f0f0' : '#fff'),
              border: selected ? '1px solid #F5A623' : (isOther ? '1px solid #aaa' : '1px solid #ccc'),
              boxShadow: selected ? '0 0 0 3px rgba(245,166,35,0.35)' : undefined,
              color: selected ? '#7A6A3A' : (isOther ? '#888' : '#333'),
              fontStyle: isOther ? 'italic' : 'normal',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {codeText ? `[${codeText}] ` : ''}{displayLabel}
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </g>
  );
};

export default ThickPathEdge;
